import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import FunnelWidget from './FunnelWidget';
import {
  computeFunnel,
  FunnelStepDefinition,
  FunnelStepResult,
} from '../utils/computeFunnel';
import type { EnrichedEventOut } from '../analyticsTypes';

import { Icon } from '@liftango/ui-icons';
import { ICON_FALLBACKS } from '../../../../utils/iconFallbacks';
export interface CustomFunnelDefinition {
  id: string;
  name: string;
  events: string[]; // ordered step event names
}

const STORAGE_KEY = 'atlas_admin_analytics_custom_funnels';
const MAX_STEPS = 5;
const MIN_STEPS = 2;

function readDefinitions(): CustomFunnelDefinition[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as CustomFunnelDefinition[];
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (d) =>
        typeof d?.id === 'string' &&
        typeof d?.name === 'string' &&
        Array.isArray(d?.events) &&
        d.events.every((s) => typeof s === 'string'),
    );
  } catch {
    return [];
  }
}

function writeDefinitions(defs: CustomFunnelDefinition[]) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(defs));
  } catch {
    // Storage unavailable.
  }
}

interface CustomFunnelsProps {
  scrape: EnrichedEventOut[];
  loading: boolean;
}

export default function CustomFunnels({ scrape, loading }: CustomFunnelsProps) {
  const [defs, setDefs] = useState<CustomFunnelDefinition[]>(() => readDefinitions());
  const [editorOpen, setEditorOpen] = useState(false);
  const [draftName, setDraftName] = useState('');
  const [draftSteps, setDraftSteps] = useState<string[]>(['', '']);

  const reset = () => {
    setDraftName('');
    setDraftSteps(['', '']);
  };

  const openEditor = () => {
    reset();
    setEditorOpen(true);
  };

  const save = () => {
    const name = draftName.trim();
    const events = draftSteps.map((s) => s.trim()).filter(Boolean);
    if (!name || events.length < MIN_STEPS) return;
    const next: CustomFunnelDefinition = {
      id: `${Date.now()}`,
      name,
      events,
    };
    const merged = [...defs, next];
    setDefs(merged);
    writeDefinitions(merged);
    setEditorOpen(false);
    reset();
  };

  const remove = (id: string) => {
    const next = defs.filter((d) => d.id !== id);
    setDefs(next);
    writeDefinitions(next);
  };

  const stepDefsById = useMemo(() => {
    const m = new Map<string, FunnelStepDefinition[]>();
    for (const d of defs) {
      m.set(
        d.id,
        d.events.map((e, idx) => ({
          id: `${d.id}-${idx}`,
          label: e,
          events: [e],
        })),
      );
    }
    return m;
  }, [defs]);

  const stepResultsById = useMemo(() => {
    const m = new Map<string, FunnelStepResult[]>();
    for (const d of defs) {
      const steps = stepDefsById.get(d.id);
      if (!steps) continue;
      m.set(d.id, computeFunnel(scrape, steps));
    }
    return m;
  }, [defs, scrape, stepDefsById]);

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Icon d={ICON_FALLBACKS.FilterAlt} size={20} />
        <Typography variant="subtitle1" fontWeight={700}>
          Custom funnels
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Saved locally
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Button size="small" variant="outlined" startIcon={<Icon name="plus" size={20} />} onClick={openEditor}>
          Add funnel
        </Button>
      </Stack>

      {defs.length === 0 ? (
        <Card variant="outlined" sx={{ borderStyle: 'dashed' }}>
          <CardContent sx={{ textAlign: 'center', py: 3 }}>
            <Typography variant="body2" color="text.secondary">
              Define your own funnels (2–{MAX_STEPS} steps). Stored in this browser.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }, gap: 3 }}>
          {defs.map((d) => (
            <Box key={d.id} sx={{ position: 'relative' }}>
              <FunnelWidget
                title={d.name}
                icon={<Icon d={ICON_FALLBACKS.FilterAlt} size={20} />}
                steps={stepResultsById.get(d.id) ?? []}
                loading={loading}
                helperText={d.events.join(' → ')}
              />
              <Tooltip title="Delete funnel" arrow>
                <IconButton
                  size="small"
                  onClick={() => remove(d.id)}
                  sx={{ position: 'absolute', top: 4, right: 4 }}
                  aria-label={`Delete funnel ${d.name}`}
                >
                  <Icon name="trash" size={20} />
                </IconButton>
              </Tooltip>
            </Box>
          ))}
        </Box>
      )}

      <Dialog open={editorOpen} onClose={() => setEditorOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>New custom funnel</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Funnel name"
              size="small"
              value={draftName}
              onChange={(e) => setDraftName(e.target.value)}
              fullWidth
              autoFocus
            />
            <Typography variant="caption" color="text.secondary">
              Add 2 to {MAX_STEPS} event names. Steps must be in the order the user takes them.
            </Typography>
            {draftSteps.map((step, idx) => (
              <Stack key={idx} direction="row" spacing={1}>
                <TextField
                  label={`Step ${idx + 1}`}
                  size="small"
                  value={step}
                  onChange={(e) => {
                    const next = [...draftSteps];
                    next[idx] = e.target.value;
                    setDraftSteps(next);
                  }}
                  fullWidth
                  placeholder="event_name"
                />
                {draftSteps.length > MIN_STEPS && (
                  <IconButton
                    size="small"
                    onClick={() => setDraftSteps(draftSteps.filter((_, i) => i !== idx))}
                    aria-label={`Remove step ${idx + 1}`}
                  >
                    <Icon name="trash" size={20} />
                  </IconButton>
                )}
              </Stack>
            ))}
            {draftSteps.length < MAX_STEPS && (
              <Button
                size="small"
                startIcon={<Icon name="plus" size={20} />}
                onClick={() => setDraftSteps([...draftSteps, ''])}
                sx={{ alignSelf: 'flex-start' }}
              >
                Add step
              </Button>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditorOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={save}
            disabled={!draftName.trim() || draftSteps.map((s) => s.trim()).filter(Boolean).length < MIN_STEPS}
          >
            Save funnel
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
